import type { CallDetail, Me, TranscriptSegmentView } from '@smarton-pbx/shared';
import { ArrowLeft, Check, CheckCheck, Download, FileText, Loader2, Lock, Pencil, Play, RotateCcw, UserPlus } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { Dialog, Loading, Notice, Pill } from '../components/ui';
import { language, locale, message, rich, t, tp } from '../i18n';
import { formatNumber, outcome, refusalText } from '../lib/calls';
import { CallBackButton, directions, duration } from './Calls';
import { MetricsCard, ReviewCard } from './Review';
import { CallPathCard } from './flows/CallPath';
import { ContactForm } from './contacts';
import { VoicemailMessage } from './Voicemail';

const languageNames = new Intl.DisplayNames([language], { type: 'language' });

/** "41 s", or "3 min 5 s". */
function seconds(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

function clock(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Marks the searched-for words in a segment. */
function withMatches(text: string, words: string[]): ReactNode {
  if (!words.length) return text;
  const pattern = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  return text.split(pattern).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : <Fragment key={i}>{part}</Fragment>));
}

/**
 * One transcript line being corrected: saved when the person leaves it
 * (Enter moves to the next line, Escape puts it back).
 */
function LineEditor({ callId, segment, onSaved }: { callId: string; segment: TranscriptSegmentView; onSaved: (line: TranscriptSegmentView) => void }) {
  const [text, setText] = useState(segment.text);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string>();

  async function save() {
    if (text.trim() === segment.text) return;
    setState('saving');
    setError(undefined);
    try {
      const line = await api.correctTranscriptLine(callId, segment.id, text);
      setText(line.text);
      onSaved(line);
      setState('saved');
    } catch (err) {
      setState('idle');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <textarea
        value={text}
        rows={Math.max(1, Math.ceil(text.length / 56))}
        onChange={(e) => {
          setState('idle');
          setText(e.target.value);
        }}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setText(segment.text);
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const next = e.currentTarget.closest('li')?.nextElementSibling?.querySelector('textarea');
            if (next) next.focus();
            else e.currentTarget.blur();
          }
        }}
        aria-label={t('What was said')}
        placeholder={t('Nothing was said')}
        data-testid="line-text"
      />
      {state === 'saving' && <span className="line-state subtle">{t('Saving…')}</span>}
      {state === 'saved' && (
        <span className="line-state subtle" data-testid="line-saved">
          <Check size={12} aria-hidden /> {t('Saved')}
        </span>
      )}
      {error && <span className="line-state danger-text">{error}</span>}
    </>
  );
}

export function CallPage({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  // Opened from another call's evidence: start playing at that moment.
  const startAt = useRef<number | null>(params.has('t') ? Number(params.get('t')) : null);
  const [reloads, setReloads] = useState(0);
  const words = (params.get('q') ?? '').split(/\s+/).filter((w) => w.length > 1 && !/^[+\d()-]+$/.test(w));
  const [call, setCall] = useState<CallDetail | null>(null);
  const [error, setError] = useState<string>();
  const [position, setPosition] = useState(0);
  const [savingContact, setSavingContact] = useState(false);
  const [retranscribing, setRetranscribing] = useState(false);
  const [retranscribeError, setRetranscribeError] = useState<string>();
  const [correcting, setCorrecting] = useState(false);
  const [checking, setChecking] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Poll while the recording or transcript is still being made.
  useEffect(() => {
    let current = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = () =>
      api.getCall(id).then(
        (detail) => {
          if (!current) return;
          setCall(detail);
          const working =
            detail.recording === 'PENDING' ||
            detail.transcriptDetail?.status === 'PENDING' ||
            (detail.recording === 'READY' && !detail.transcriptDetail) ||
            detail.review?.status === 'PENDING';
          if (working) timer = setTimeout(load, 3000);
        },
        (err: unknown) => current && setError(err instanceof Error ? err.message : String(err)),
      );
    void load();
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [id, reloads]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!call) return <Loading />;

  const direction = directions[call.direction];
  const Icon = direction.icon;
  const result = outcome(call);
  const staffName = call.userName ?? t('Staff');
  const otherName =
    call.direction === 'INTERNAL' ? (call.toUserName ?? call.toNumber) : formatNumber(call.direction === 'INBOUND' ? call.fromNumber : call.toNumber);
  const outsideNumber = call.direction === 'INBOUND' ? call.fromNumber : call.toNumber;
  const party = call.contactId ? (
    <Link to={`/contacts/${call.contactId}`} className="plain-link" data-testid="call-page-contact">
      {call.contactName}
    </Link>
  ) : (
    <span className="tabular">{otherName}</span>
  );
  const transcript = call.transcriptDetail;
  const segments = transcript?.segments ?? [];
  // The line being played; none before anything has.
  const activeIndex = position > 0 ? segments.findIndex((s) => position * 1000 >= s.startMs && position * 1000 <= s.endMs + 300) : -1;

  const playAt = (ms: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    startAt.current = null;
    audio.currentTime = ms / 1000;
    void audio.play();
  };
  const play = (segment: TranscriptSegmentView) => playAt(segment.startMs);
  const reload = () => setReloads((n) => n + 1);
  const lineSaved = (line: TranscriptSegmentView) =>
    setCall((current) =>
      current?.transcriptDetail
        ? { ...current, transcriptDetail: { ...current.transcriptDetail, segments: current.transcriptDetail.segments.map((s) => (s.id === line.id ? line : s)) } }
        : current,
    );
  async function markChecked() {
    setChecking(true);
    setRetranscribeError(undefined);
    try {
      const checked = await api.markTranscriptChecked(call!.id);
      setCall((current) => (current?.transcriptDetail ? { ...current, transcriptDetail: { ...current.transcriptDetail, checked } } : current));
      setCorrecting(false);
    } catch (err) {
      setRetranscribeError(err instanceof Error ? err.message : String(err));
    } finally {
      setChecking(false);
    }
  }
  async function transcribeAgain() {
    setRetranscribing(true);
    setRetranscribeError(undefined);
    try {
      await api.transcribeAgain(call!.id);
      reload();
    } catch (err) {
      setRetranscribeError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetranscribing(false);
    }
  }

  return (
    <div className="stack">
      <Link to="/calls" className="back-link">
        <ArrowLeft size={16} aria-hidden /> {t('Calls')}
      </Link>
      <div className="card card-body call-head">
        <span className="avatar square large" style={{ color: direction.color, background: 'var(--surface-2)' }}>
          <Icon size={22} aria-hidden />
        </span>
        <div className="grow">
          <h1 className="call-title">
            {call.direction === 'INBOUND'
              ? rich('Incoming call from {name}', { name: party })
              : call.direction === 'OUTBOUND'
                ? rich('Outgoing call with {name}', { name: party })
                : rich('Internal call with {name}', { name: party })}
          </h1>
          <div className="call-meta">
            <span>{new Date(call.startedAt).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}</span>
            {call.userName && <span>{call.direction === 'INTERNAL' ? t('From {name}', { name: call.userName }) : call.userName}</span>}
            {call.regionName && <span>{call.regionName}</span>}
            {call.durationSeconds !== null && <span className="tabular">{duration(call.durationSeconds)}</span>}
            {call.direction !== 'INTERNAL' && (
              <span className="tabular">
                {formatNumber(call.fromNumber)} → {formatNumber(call.toNumber)}
              </span>
            )}
          </div>
        </div>
        {call.direction !== 'INTERNAL' && !call.contactId && outsideNumber.startsWith('+') && (
          <button type="button" className="secondary small" onClick={() => setSavingContact(true)} data-testid="call-page-save-contact">
            <UserPlus size={15} aria-hidden /> {t('Save contact')}
          </button>
        )}
        <CallBackButton call={call} me={me} label />
        <span data-testid="call-detail-outcome" title={call.outcome === 'REJECTED' ? message(refusalText(call.rejectReason)) : undefined}>
          <Pill {...(result.kind && { kind: result.kind })}>{result.label}</Pill>
        </span>
      </div>

      {call.throughFlow && me.role !== 'AGENT' && <CallPathCard callId={call.id} />}
      <Dialog open={savingContact} onClose={() => setSavingContact(false)} title={t('New contact')} wide>
        {savingContact && (
          <ContactForm
            me={me}
            contact={null}
            initialNumber={outsideNumber}
            onCancel={() => setSavingContact(false)}
            onSaved={() => {
              setSavingContact(false);
              reload();
            }}
          />
        )}
      </Dialog>

      {call.voicemail && (
        <div className="card" data-testid="call-voicemail">
          <div className="card-header">
            <h2>{t('Voicemail')}</h2>
            <span className="subtle tabular" style={{ fontSize: '0.85rem' }}>
              {call.voicemail.durationSeconds} s
            </span>
          </div>
          <div className="card-body stack voicemail-card" style={{ gap: '0.7rem' }}>
            <VoicemailMessage message={call.voicemail} />
          </div>
        </div>
      )}

      {call.recording && (
        <div className="card">
          <div className="card-header">
            <h2>{t('Recording')}</h2>
            {call.recording === 'READY' && (
              <a className="button secondary small" href={api.callRecordingUrl(call.id, true)} download>
                <Download size={15} aria-hidden /> {t('Download')}
              </a>
            )}
          </div>
          <div className="card-body">
            {call.recording === 'READY' ? (
              <>
                <audio
                  ref={audioRef}
                  controls
                  // Nothing loads until someone presses play: each play is audited.
                  preload="none"
                  src={api.callRecordingUrl(call.id)}
                  onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                  onPlay={(e) => {
                    if (startAt.current !== null) {
                      e.currentTarget.currentTime = startAt.current / 1000;
                      startAt.current = null;
                    }
                  }}
                  style={{ width: '100%' }}
                  data-testid="recording-player"
                />
                <p className="subtle" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  {startAt.current !== null ? `${t('Plays from {time}.', { time: clock(startAt.current) })} ` : ''}
                  {t('Every play is recorded in the audit log.')}
                </p>
                {call.pauses.length > 0 && (
                  <div className="pause-marks" data-testid="recording-pauses">
                    {call.pauses.map((p) => (
                      <Pill key={p.atMs}>
                        {p.durationMs !== null
                          ? t('Paused at {time} for {seconds} s', { time: clock(p.atMs), seconds: Math.max(1, Math.round(p.durationMs / 1000)) })
                          : t('Paused at {time} until the end', { time: clock(p.atMs) })}
                      </Pill>
                    ))}
                  </div>
                )}
              </>
            ) : call.recording === 'PENDING' ? (
              <p className="muted row" style={{ gap: '0.5rem' }}>
                <Loader2 size={16} className="spin" aria-hidden /> {t('Preparing the recording…')}
              </p>
            ) : (
              <Notice kind="error">{t('The recording could not be processed.')}</Notice>
            )}
          </div>
        </div>
      )}

      {call.review && <ReviewCard review={call.review} callId={call.id} me={me} title={t('AI review')} onPlay={playAt} onChanged={reload} />}
      {call.conversationReview && (
        <ReviewCard
          review={call.conversationReview}
          callId={call.id}
          me={me}
          title={tp(call.conversationReview.calls?.length ?? 2, 'Across {n} call with this customer', 'Across {n} calls with this customer')}
          onPlay={playAt}
          onChanged={reload}
        />
      )}
      {call.metrics && call.direction !== 'INTERNAL' && <MetricsCard metrics={call.metrics} staffName={staffName} />}

      {(transcript || call.recording === 'READY') && (
        <div className="card">
          <div className="card-header">
            <h2>{t('Transcript')}</h2>
            <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {transcript?.status === 'DONE' && (
                <span className="subtle" style={{ fontSize: '0.85rem' }} data-testid="transcript-meta">
                  {transcript.language ? languageNames.of(transcript.language) : ''}
                  {transcript.languageProbability !== null ? ` ${t('({percent}% sure)', { percent: Math.round(transcript.languageProbability * 100) })}` : ''}
                  {transcript.model ? ` · ${transcript.model}` : ''}
                  {transcript.processingMs !== null ? ` · ${t('took {time}', { time: seconds(transcript.processingMs) })}` : ''}
                  {transcript.processingMs !== null && transcript.audioSeconds
                    ? ` ${t('({ratio}× the call)', {
                        ratio: (transcript.processingMs / 1000 / transcript.audioSeconds).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                      })}`
                    : ''}
                </span>
              )}
              {transcript?.checked && !correcting && (
                <span data-testid="transcript-checked-by" title={new Date(transcript.checked.at).toLocaleString(locale)}>
                  <Pill kind="success">
                    <CheckCheck size={13} aria-hidden /> {transcript.checked.by ? t('Checked by {name}', { name: transcript.checked.by }) : t('Checked')}
                  </Pill>
                </span>
              )}
              {transcript?.editable && segments.length > 0 && !correcting && (
                <button type="button" className="secondary small" onClick={() => setCorrecting(true)} data-testid="correct-transcript">
                  <Pencil size={14} aria-hidden /> {t('Correct')}
                </button>
              )}
              {correcting && (
                <>
                  <button type="button" className="secondary small" onClick={() => setCorrecting(false)} data-testid="done-correcting">
                    {t('Done')}
                  </button>
                  <button
                    type="button"
                    className="small"
                    disabled={checking}
                    onClick={() => void markChecked()}
                    title={t("Every line is right now: the ones you didn't change count as checked too")}
                    data-testid="mark-transcript-checked"
                  >
                    <CheckCheck size={14} aria-hidden /> {t('Transcript checked')}
                  </button>
                </>
              )}
              {!correcting && me.role !== 'AGENT' && call.recording === 'READY' && transcript && transcript.status !== 'PENDING' && (
                <button
                  type="button"
                  className="secondary small"
                  disabled={retranscribing}
                  onClick={() => void transcribeAgain()}
                  title={t('With the current transcription settings, e.g. another model')}
                  data-testid="transcribe-again"
                >
                  <RotateCcw size={14} aria-hidden /> {t('Transcribe again')}
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {retranscribeError && <Notice kind="error">{retranscribeError}</Notice>}
            {!transcript || transcript.status === 'PENDING' ? (
              <p className="muted row" style={{ gap: '0.5rem' }}>
                <Loader2 size={16} className="spin" aria-hidden /> {t('Transcribing… this can take a few minutes.')}
              </p>
            ) : transcript.status === 'SKIPPED' ? (
              <p className="muted">{t('This call wasn’t transcribed.')}</p>
            ) : transcript.status === 'FAILED' ? (
              <Notice kind="error">{t('Transcription failed. The recording is unaffected.')}</Notice>
            ) : segments.length === 0 ? (
              <p className="muted">{t('No speech was found in this call.')}</p>
            ) : (
              <>
                {correcting ? (
                  <p className="subtle transcript-note" data-testid="correcting-note">
                    <Pencil size={14} aria-hidden /> {t('Fix the words; capitals and punctuation don’t matter. Each line is saved when you leave it. Click a name to hear the line.')}
                    {transcript.keptForTraining ? ` ${t('Your corrections also teach the transcription.')}` : ''}
                  </p>
                ) : (
                  <p className="subtle transcript-note">
                    <FileText size={14} aria-hidden /> {t('Machine transcript: it may contain mistakes. Click a line to hear it.')}
                  </p>
                )}
                <ol className={`transcript ${correcting ? 'correcting' : ''}`} data-testid="transcript">
                  {segments.map((segment, i) => (
                    <li key={segment.id} className={`${segment.speaker === 'STAFF' ? 'staff' : 'other'} ${i === activeIndex ? 'active' : ''}`}>
                      {correcting ? (
                        <div className="line-edit" data-testid="line-edit" data-speaker={segment.speaker}>
                          <button type="button" className="who" onClick={() => play(segment)} title={t('Play this line')}>
                            <Play size={11} aria-hidden /> {segment.speaker === 'STAFF' ? staffName : otherName} · <span className="tabular">{clock(segment.startMs)}</span>
                          </button>
                          {segment.locked ? (
                            <p className="locked-line" title={t('Personal details in this line are hidden from you. A manager or admin can correct it.')} data-testid="line-locked">
                              <Lock size={12} aria-hidden /> {segment.text}
                            </p>
                          ) : (
                            <LineEditor callId={call.id} segment={segment} onSaved={lineSaved} />
                          )}
                        </div>
                      ) : (
                        <button type="button" onClick={() => play(segment)} data-testid="segment" data-speaker={segment.speaker}>
                          <span className="who">
                            {segment.speaker === 'STAFF' ? staffName : otherName} · <span className="tabular">{clock(segment.startMs)}</span>
                            {segment.edited && (
                              <span
                                className="corrected"
                                title={
                                  segment.edited.by
                                    ? t('The transcription wrote: “{text}”. Corrected by {name}, {when}.', {
                                        text: segment.edited.originalText,
                                        name: segment.edited.by,
                                        when: new Date(segment.edited.at).toLocaleString(locale),
                                      })
                                    : t('The transcription wrote: “{text}”. Corrected {when}.', {
                                        text: segment.edited.originalText,
                                        when: new Date(segment.edited.at).toLocaleString(locale),
                                      })
                                }
                                data-testid="line-corrected"
                              >
                                {' '}
                                · <Pencil size={10} aria-hidden /> {t('corrected')}
                              </span>
                            )}
                          </span>
                          <span className="said">{segment.text ? withMatches(segment.text, words) : <span className="muted">{t('(nothing said)')}</span>}</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
